import { useAuthFrontendApis } from '@propelauth/frontend-apis-react';
import { useAuthInfo } from '@propelauth/react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Eye, EyeOff, Mail } from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';

interface LoginFormProps {
  email: string;
  onSuccess?: (user: any) => void;
  onForgotPassword?: () => void;
  onSwitchToSignup?: () => void;
}

type LoginStep = 'password-login' | 'magic-link-sent';

export function LoginForm({
  email,
  onSuccess,
  onForgotPassword,
  onSwitchToSignup,
}: LoginFormProps) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isMagicLinkLoading, setIsMagicLinkLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>('password-login');
  const [errors, setErrors] = useState<{ password?: string; general?: string }>({});

  const { emailPasswordLogin, passwordlessLogin } = useAuthFrontendApis();
  const { refreshAuthInfo, user } = useAuthInfo();

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPasswordLoading(true);
    setErrors({});

    try {
      const response = await emailPasswordLogin({ email, password });

      await response.handle({
        success: async data => {
          console.log('Login success data:', data);
          await refreshAuthInfo();

          // Pass the login response data to the auth controller
          onSuccess?.({
            email: email,
            loginState: data?.login_state,
          });
        },
        badRequest: error => {
          console.log('BadRequest error:', error);
          const errorMessage = 'Invalid email or password';
          setErrors({
            general: errorMessage,
          });
        },
        unexpectedOrUnhandled: error => {
          console.log('Unexpected error:', error);
          const errorMessage = 'Invalid email or password';
          setErrors({ general: errorMessage });
        },
      });
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleMagicLinkLogin = async () => {
    setIsMagicLinkLoading(true);
    setErrors({});

    try {
      const response = await passwordlessLogin({
        email,
        create_if_doesnt_exist: false,
      });

      await response.handle({
        success: () => {
          setStep('magic-link-sent');
        },
        passwordlessLoginDisabled: error => {
          setErrors({ general: 'Passwordless login is not enabled' });
        },
        userAccountDisabled: error => {
          setErrors({ general: error.user_facing_error || 'Account is disabled' });
        },
        userAccountLocked: error => {
          setErrors({ general: error.user_facing_error || 'Account is locked' });
        },
        badRequest: error => {
          setErrors({
            general: error.user_facing_error || 'Invalid email address',
          });
        },
        unexpectedOrUnhandled: error => {
          setErrors({ general: 'An unexpected error occurred. Please try again.' });
        },
      });
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsMagicLinkLoading(false);
    }
  };

  const handleBackToPassword = () => {
    setStep('password-login');
    setErrors({});
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <AnimatePresence mode="wait">
        {step === 'password-login' && (
          <motion.div
            key="password-login"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            className="space-y-6"
          >
            {errors.general && (
              <Alert variant="destructive">
                <AlertDescription>{errors.general}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handlePasswordLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email-login" className="text-sm font-medium text-neutral-950">
                  Email
                </Label>
                <Input
                  id="email-login"
                  type="email"
                  value={email}
                  className="h-11 border-neutral-200 text-neutral-950"
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-neutral-950">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="pr-10 h-11 border-neutral-200 focus:ring-black focus:border-black text-neutral-950"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute transition-colors -translate-y-1/2 text-neutral-400 right-3 top-1/2 hover:text-neutral-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-sm text-red-600">{errors.password}</p>}
              </div>

              <div className="space-y-3">
                <Button
                  type="submit"
                  className="w-full font-medium bg-black h-11 hover:bg-gray-800"
                  disabled={isPasswordLoading}
                >
                  {isPasswordLoading ? (
                    <>
                      <Spinner size={16} className="mr-2" color="white" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    className="text-sm transition-colors cursor-pointer text-neutral-600 hover:text-neutral-900 hover:underline"
                  >
                    Forgot your password?
                  </button>
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-neutral-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="px-2 my-2 bg-white text-neutral-500">Or</span>
                  </div>
                </div>

                <Button
                  type="button"
                  onClick={handleMagicLinkLogin}
                  variant="outline"
                  className="w-full h-11 border-neutral-200 hover:bg-neutral-50 text-neutral-600"
                  disabled={isMagicLinkLoading}
                >
                  {isMagicLinkLoading ? (
                    <>
                      <Spinner size={16} className="mr-2" />
                      Sending magic link...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Magic Link
                    </>
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}

        {step === 'magic-link-sent' && (
          <motion.div
            key="magic-link-sent"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email-sent" className="text-sm font-medium text-neutral-950">
                  Email
                </Label>
                <Input
                  id="email-sent"
                  type="email"
                  value={email}
                  className="h-11 border-neutral-200 text-neutral-950"
                  disabled
                />
              </div>

              <div className="p-4 border border-green-200 rounded-lg bg-green-50">
                <div className="flex items-center space-x-2">
                  <Mail className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-sm font-medium text-green-800">Magic link sent!</p>
                    <p className="text-sm text-green-700">
                      Check your email for a login link. Click the link to sign in instantly.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="button"
                onClick={handleMagicLinkLogin}
                variant="outline"
                className="w-full h-11 border-neutral-200 hover:bg-neutral-50 text-neutral-600"
                disabled={isMagicLinkLoading}
              >
                {isMagicLinkLoading ? (
                  <>
                    <Spinner size={16} className="mr-2" />
                    Resending...
                  </>
                ) : (
                  'Resend Magic Link'
                )}
              </Button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleBackToPassword}
                className="inline-flex items-center text-sm transition-colors text-neutral-600 hover:text-neutral-900"
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Back to password
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="text-center">
        <p className="text-sm text-neutral-600">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToSignup}
            className="font-medium text-black transition-colors hover:text-gray-700 hover:underline"
          >
            Sign up
          </button>
        </p>
      </div>
    </motion.div>
  );
}
